import { useEffect, useRef, useState } from "react";
import { Accessibility } from "lucide-react";
import "./AccessibilityWidget.css";

const STORAGE_KEY = "sparkling-a11y-prefs";

const TEXT_SIZES = ["normal", "grande", "extra-grande"];

const DEFAULT_PREFS = {
  textSize: "normal",
  highContrast: false,
  reduceMotion: false,
  underlineLinks: false,
};

const loadPrefs = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_PREFS;
  }
};

const applyPrefs = (prefs) => {
  const root = document.documentElement;

  root.classList.remove("a11y-text-grande", "a11y-text-extra-grande");
  if (prefs.textSize === "grande") root.classList.add("a11y-text-grande");
  if (prefs.textSize === "extra-grande") root.classList.add("a11y-text-extra-grande");

  root.classList.toggle("a11y-high-contrast", prefs.highContrast);
  root.classList.toggle("a11y-underline-links", prefs.underlineLinks);
  root.classList.toggle("a11y-reduce-motion", prefs.reduceMotion);

  if (prefs.reduceMotion) {
    document.querySelectorAll("video[autoplay]").forEach((video) => video.pause());
  }

  window.dispatchEvent(new CustomEvent("a11y:reduce-motion", { detail: { enabled: prefs.reduceMotion } }));
};

const AccessibilityWidget = () => {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(loadPrefs);
  const panelRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    applyPrefs(prefs);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      // localStorage no disponible (modo privado, etc.) — no bloqueamos la función
    }
  }, [prefs]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && !buttonRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const cycleTextSize = () => {
    setPrefs((p) => {
      const idx = TEXT_SIZES.indexOf(p.textSize);
      const next = TEXT_SIZES[(idx + 1) % TEXT_SIZES.length];
      return { ...p, textSize: next };
    });
  };

  const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));

  const reset = () => setPrefs(DEFAULT_PREFS);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="a11y-toggle-button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="a11y-panel"
        aria-label="Opciones de accesibilidad"
      >
        <Accessibility size={28} aria-hidden="true" />
      </button>

      {open && (
        <div
          id="a11y-panel"
          ref={panelRef}
          className="a11y-panel"
          role="dialog"
          aria-label="Opciones de accesibilidad"
        >
          <div className="a11y-panel-header">
            <h2>Accesibilidad</h2>
            <button
              type="button"
              className="a11y-close-button"
              onClick={() => { setOpen(false); buttonRef.current?.focus(); }}
              aria-label="Cerrar panel de accesibilidad"
            >
              ✕
            </button>
          </div>

          <div className="a11y-panel-body">
            <button type="button" className="a11y-option-button" onClick={cycleTextSize}>
              <span>Tamaño del texto</span>
              <span className="a11y-option-value">
                {prefs.textSize === "normal" ? "Normal" : prefs.textSize === "grande" ? "Grande" : "Extra grande"}
              </span>
            </button>

            <button
              type="button"
              className="a11y-option-button"
              onClick={() => toggle("highContrast")}
              aria-pressed={prefs.highContrast}
            >
              <span>Alto contraste</span>
              <span className="a11y-option-value">{prefs.highContrast ? "Activado" : "Desactivado"}</span>
            </button>

            <button
              type="button"
              className="a11y-option-button"
              onClick={() => toggle("underlineLinks")}
              aria-pressed={prefs.underlineLinks}
            >
              <span>Subrayar enlaces</span>
              <span className="a11y-option-value">{prefs.underlineLinks ? "Activado" : "Desactivado"}</span>
            </button>

            <button
              type="button"
              className="a11y-option-button"
              onClick={() => toggle("reduceMotion")}
              aria-pressed={prefs.reduceMotion}
            >
              <span>Reducir movimiento</span>
              <span className="a11y-option-value">{prefs.reduceMotion ? "Activado" : "Desactivado"}</span>
            </button>

            <button type="button" className="a11y-reset-button" onClick={reset}>
              Restablecer
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AccessibilityWidget;
